import React, { createContext, useContext, useEffect, useState } from "react";

// Dominios HOME de WAIA (frontend + API + local). En un dominio HOME NO se
// fetchea whitelabel → whiteLabel=null → panel branded WAIA estable, sin flash.
// ⚠ "waia.zzia.info" es el dominio del FRONTEND (antes faltaba y caía al fetch
// de la fila WL id=2 → panel neutro de solo-logo). "apiwaia.zzia.info" es la API.
const WAIA_DOMAINS = [
  "waia.zzia.info",
  "www.waia.zzia.info",
  "apiwaia.zzia.info",
  "localhost",
  "127.0.0.1",
];

// Resuelto SINCRÓNICAMENTE en el primer render → el panel derecho ya pinta
// correcto desde el frame 0 (branded solo en HOME, neutro en el resto): mata el
// flash de branding WAIA en dominios de revendedor.
const IS_HOME_DOMAIN =
  typeof window !== "undefined" &&
  WAIA_DOMAINS.includes(window.location.hostname);

const WhiteLabelContext = createContext({
  whiteLabel: null,
  isLoading: true,
  isHomeDomain: false,
});

export const WhiteLabelProvider = ({ children }) => {
  const [whiteLabel, setWhiteLabel] = useState(null);
  // HOME resuelve al instante (no hay fetch) → isLoading arranca false ahí.
  const [isLoading, setIsLoading] = useState(!IS_HOME_DOMAIN);

  useEffect(() => {
    const hostname = window.location.hostname;

    if (IS_HOME_DOMAIN) {
      setIsLoading(false);
      return;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || (window.ENV && window.ENV.VITE_BACKEND_URL) || "";
    fetch(`${backendUrl.replace(/\/$/, "")}/api/whitelabel?domain=${encodeURIComponent(hostname)}`)
      .then(async (res) => {
        if (res.status === 404) {
          // Dominio no registrado → mostrar branding WAIA por defecto
          // No redirigir — error transitorio no debe expulsar al usuario
          setIsLoading(false);
          return;
        }
        if (!res.ok) throw new Error("whitelabel fetch failed");
        const data = await res.json();
        setWhiteLabel(data);
        setIsLoading(false);
      })
      .catch(() => {
        // Red error → mostrar WAIA sin romper
        setIsLoading(false);
      });
  }, []);

  // Título de la pestaña = nombre de la instancia (marca blanca). Sin config
  // (HOME o dominio desconocido) se deja el <title> por defecto del index.html.
  // Ojo: NotificationsPopOver también escribe document.title (con el contador de
  // no leídos, mismo `whiteLabel.name`), pero solo dentro del layout logueado —
  // esto cubre login y el arranque previo a su montaje.
  useEffect(() => {
    if (whiteLabel && whiteLabel.name) {
      document.title = whiteLabel.name;
    }
  }, [whiteLabel]);

  return (
    <WhiteLabelContext.Provider value={{ whiteLabel, isLoading, isHomeDomain: IS_HOME_DOMAIN }}>
      {children}
    </WhiteLabelContext.Provider>
  );
};

export const useWhiteLabel = () => useContext(WhiteLabelContext);

// Fallback WAIA-home: assets del bundle (copiados de la fila WL id=2 en
// frontend/public, feat_287). Sin config (WAIA home o dominio desconocido) el
// logo/chat-empty debe ser el de WAIA, NO el logo de texto /logo-app.svg ni null
// (efecto colateral de feat_285/286 que sacó waia.zzia.info del fetch WL).
const WAIA_LOGO_LIGHT = "/waia-logo-sidebar.png";
const WAIA_LOGO_DARK = "/waia-logo-sidebar-dark.png";
const WAIA_CHAT_EMPTY_LIGHT = "/waia-chat-empty.png";
const WAIA_CHAT_EMPTY_DARK = "/waia-chat-empty-dark.png";

export const getWLLogo = (whiteLabel, darkMode, fallback) => {
  const wlFallback = fallback || (darkMode ? WAIA_LOGO_DARK : WAIA_LOGO_LIGHT);
  if (!whiteLabel) return wlFallback;
  if (darkMode) return whiteLabel.logoUrlDark || whiteLabel.logoUrl || wlFallback;
  return whiteLabel.logoUrl || whiteLabel.logoUrlDark || wlFallback;
};

export const getWLChatImage = (whiteLabel, darkMode) => {
  const wlFallback = darkMode ? WAIA_CHAT_EMPTY_DARK : WAIA_CHAT_EMPTY_LIGHT;
  if (!whiteLabel) return wlFallback;
  if (darkMode) return whiteLabel.chatEmptyImageUrlDark || whiteLabel.chatEmptyImageUrl || wlFallback;
  return whiteLabel.chatEmptyImageUrl || whiteLabel.chatEmptyImageUrlDark || wlFallback;
};

export default WhiteLabelContext;
